import React, { useEffect, useRef, useState } from 'react';
import { refreshTokenAndSave, isAuthenticated, getAuthToken } from '../services/authService';
import { useAuth } from '../context/AuthContext';

/**
 * Component quản lý việc refresh token tự động
 * Token sẽ được refresh trước khi hết hạn để đảm bảo phiên làm việc liên tục
 */
const TokenRefreshManager: React.FC = () => {
  const { isAuthenticated: authContextAuthenticated, setIsLoggedIn } = useAuth();
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Hàm check token hết hạn
  const isTokenExpired = (): boolean => {
    const token = getAuthToken();
    if (!token) return true;

    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) return true;

      const payload = JSON.parse(atob(tokenParts[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();

      return currentTime >= expirationTime;
    } catch (error) {
      console.error('Lỗi khi kiểm tra token hết hạn:', error);
      return true;
    }
  };

  // Kiểm tra và refresh token nếu cần thiết
  const checkAndRefreshToken = async () => {
    if (!authContextAuthenticated) return;
    if (isRefreshing) return;

    try {
      setIsRefreshing(true);
      // Nếu token đã hết hạn hoặc sắp hết hạn (còn 5 phút)
      const token = getAuthToken();
      if (!token) {
        setIsLoggedIn(false);
        setIsRefreshing(false);
        return;
      }

      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        setIsLoggedIn(false);
        setIsRefreshing(false);
        return;
      }

      const payload = JSON.parse(atob(tokenParts[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiry = expirationTime - currentTime;

      // Nếu token đã hết hạn hoặc sắp hết hạn (còn 5 phút)
      if (timeUntilExpiry < 5 * 60 * 1000) {
        console.log('Token sắp hết hạn hoặc đã hết hạn, đang refresh...');
        const result = await refreshTokenAndSave();
        if (result.result === 'SUCCESS') {
          console.log('Refresh token thành công');
          setupRefreshTimer();
        } else {
          console.error('Refresh token thất bại:', result.message);
          // Nếu refresh thất bại, kiểm tra xem còn đăng nhập không
          if (!isAuthenticated()) {
            setIsLoggedIn(false);
          }
        }
      } else {
        // Nếu token còn hạn, thiết lập timer
        setupRefreshTimer();
      }
    } catch (error) {
      console.error('Lỗi khi kiểm tra và refresh token:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    // Chỉ thiết lập timer nếu đã đăng nhập
    if (authContextAuthenticated) {
      // Kiểm tra và refresh token ngay khi component được mount
      checkAndRefreshToken();

      // Thêm event listener để kiểm tra token khi tab được active
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          checkAndRefreshToken();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        // Dọn dẹp timer và event listener khi component unmount
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    } else {
      // Xóa timer nếu đã đăng xuất
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    }
  }, [authContextAuthenticated]);

  // Thiết lập timer để refresh token
  const setupRefreshTimer = () => {
    try {
      // Lấy token để phân tích thời gian hết hạn
      const token = getAuthToken();
      if (!token) {
        console.log('Không tìm thấy token');
        return;
      }

      try {
        // Phân tích JWT để lấy thời gian hết hạn
        const tokenParts = token.split('.');
        if (tokenParts.length !== 3) {
          console.error('Token không đúng định dạng JWT');
          return;
        }

        const payload = JSON.parse(atob(tokenParts[1]));
        const expirationTime = payload.exp * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const timeUntilExpiry = expirationTime - currentTime;

        // Tính thời gian refresh (trước khi hết hạn 5 phút)
        const refreshTime = timeUntilExpiry - (5 * 60 * 1000);
        const timeToRefresh = Math.max(refreshTime, 60000); // Tối thiểu 1 phút

        console.log(`Token hết hạn vào: ${new Date(expirationTime).toLocaleString()}`);
        console.log(`Thiết lập refresh token sau ${Math.round(timeToRefresh / 60000)} phút`);

        // Xóa timer cũ nếu có
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
        }

        // Thiết lập timer mới
        refreshTimerRef.current = setTimeout(async () => {
          console.log('Đang refresh token tự động...');
          try {
            setIsRefreshing(true);
            const result = await refreshTokenAndSave();
            if (result.result === 'SUCCESS') {
              console.log('Refresh token tự động thành công');
              // Thiết lập timer mới cho lần refresh tiếp theo
              setupRefreshTimer();
            } else {
              console.error('Refresh token tự động thất bại:', result.message);
              // Nếu refresh thất bại, kiểm tra xem còn đăng nhập không
              if (!isAuthenticated()) {
                setIsLoggedIn(false);
              }
            }
          } catch (error) {
            console.error('Lỗi khi refresh token tự động:', error);
          } finally {
            setIsRefreshing(false);
          }
        }, timeToRefresh);
      } catch (error) {
        console.error('Lỗi khi phân tích JWT token:', error);
      }
    } catch (error) {
      console.error('Lỗi khi thiết lập timer refresh token:', error);
    }
  };

  // Component này không render gì cả
  return null;
};

export default TokenRefreshManager; 